import { useEffect, useState } from "react";

/**
 * ResizeObserver que aplica su callback en el frame siguiente. Si el callback cambia el estado de
 * React dentro de la propia notificación (columnas, alto de la barra), el nuevo layout vuelve a
 * redimensionar lo observado en el mismo frame y el navegador lanza "ResizeObserver loop
 * completed with undelivered notifications" (en desarrollo, el overlay de CRA lo pinta como error
 * de ejecución; p.ej. al cambiar de día en la vista de día, cuando los skeletons acortan la
 * página y aparece o desaparece la barra de scroll).
 * @returns {{observe: Function, disconnect: Function}|null} null si no hay ResizeObserver
 */
const createDeferredResizeObserver = (callback) => {
  if (typeof ResizeObserver === "undefined") return null;
  let frame = null;
  const observer = new ResizeObserver(() => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = null;
      callback();
    });
  });
  return {
    observe: (node) => observer.observe(node),
    disconnect: () => {
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    },
  };
};

// Header de la app (fuera de alcance: solo se mide, no se toca). Es `.BarraSuperiorUsuario`
// (MenuApiComponent.js), `position: fixed` cuando el tenant tiene `sticky`.
const HEADER_SELECTOR = ".BarraSuperiorUsuario";

const measureHeader = () => {
  const header = document.querySelector(HEADER_SELECTOR);
  if (!header) return 0;
  const { position } = window.getComputedStyle(header);
  if (position !== "fixed" && position !== "sticky") return 0;
  return Math.max(0, Math.round(header.getBoundingClientRect().bottom));
};

/**
 * Alto real del header fijo de la app, para colocar la barra sticky de la Agenda justo debajo y
 * para el desplazamiento del scroll al día (header + barra + 8 px, pantalla 02). Cambia con el
 * punto de corte y el tipo de menú, por eso se mide en vez de fijarlo (pantalla 16).
 */
export const useHeaderHeight = () => {
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const update = () => setHeight(measureHeader());
    update();
    const header = document.querySelector(HEADER_SELECTOR);
    const observer = createDeferredResizeObserver(update);
    if (observer && header) observer.observe(header);
    window.addEventListener("resize", update);
    // El header puede montarse después que la página (el menú llega con /interfaz).
    const retry = setTimeout(update, 500);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", update);
      clearTimeout(retry);
    };
  }, []);

  return height;
};

/**
 * Barra fija de la Agenda (Filtros + tira) justo debajo del header.
 *
 * No se usa `position: sticky`: `.App` (App.js, fuera de alcance) tiene `overflow-x: hidden;
 * overflow-y: auto`, lo que la convierte en el contenedor de referencia del sticky aunque el
 * scroll real sea el de `window`, y la barra se iría con el contenido. En su lugar, cuando el
 * ancla (el hueco que ocupa la barra en el flujo) pasa por debajo del header, la barra pasa a
 * `position: fixed` con el mismo ancho y posición horizontal que la página, y el hueco conserva su
 * alto para que el contenido no salte.
 * @returns {{fixed: boolean, left: number, width: number}}
 */
export const useFixedToolbar = (anchorRef, pageRef, headerHeight) => {
  const [box, setBox] = useState({ fixed: false, left: 0, width: 0 });

  useEffect(() => {
    let frame = null;
    const update = () => {
      frame = null;
      const anchor = anchorRef.current;
      const page = pageRef.current;
      if (!anchor || !page) return;
      const fixed = anchor.getBoundingClientRect().top < headerHeight;
      const rect = page.getBoundingClientRect();
      setBox((prev) =>
        prev.fixed === fixed && prev.left === rect.left && prev.width === rect.width ? prev : { fixed, left: rect.left, width: rect.width },
      );
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [anchorRef, pageRef, headerHeight]);

  return box;
};

/** Alto de un elemento (la barra fija), observado. */
export const useElementHeight = (ref) => {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const observer = createDeferredResizeObserver(() => setHeight(node.offsetHeight));
    if (!observer) return undefined;
    observer.observe(node);
    setHeight(node.offsetHeight);
    return () => observer.disconnect();
  }, [ref]);
  return height;
};

// Columnas por fila: las mismas que la rejilla del buscador (`.bq-grid` en Buscar.css), como en el
// resto de páginas de la OTT: 5 desde 768 px y 2 por debajo. La API devuelve hasta 5 tarjetas por
// día (AGENDA_CARDS_PER_DAY), así que una fila nunca lleva más de 5 eventos y ocupa todo el ancho.
export const AGENDA_COLUMNS = 5;
export const AGENDA_COLUMNS_MOBILE = 2;

/** "Ahora", refrescado cada 60 s: recalcula el badge EN DIRECTO/PROGRAMADO sin llamar a la API. */
export const useNowTick = (intervalMs = 60 * 1000) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
};
